import React, {
  useState, useEffect, useMemo, useCallback, Suspense,
} from 'react';
import ReactHtmlParser from 'react-html-parser';
import { BLOCKS } from '@contentful/rich-text-types';
import { useIntl } from 'react-intl';
import { WindowLocation } from '@reach/router';
import useActiveStore, {
  useActiveStoreId,
} from '../../../hooks/useActiveStore';
import {
  Contentful,
  DeviceType,
  InventoryStatus,
  ProductInventoryResponse,
  ReserveStatus,
} from '../../../types';
import Seo from '../../Seo/Seo';
import ColourPicker from '../../elements/ColourPicker/index';
import MemorySelector from '../../elements/MemorySelector';
import Gallery from '../../elements/Gallery/Gallery';
import RichText from '../../UI/RichText';
import CompatibleAccessories from '../../AccessoriesComponents/CompatibleAccessories/CompatibleAccessories';
import openChatWidget from '../../../utils/openChatWidget';
import ContextConsumer from '../../sections/CompareToolBar/CompareToolBarContext';
import useLocation from '../../../utils/location';
import { useFormatPhone } from '../../../hooks/useFormatPhone';
import { useInventoryPayload } from '../../../hooks/useInventoryApi';
import useTmsApi from '../../../hooks/useTmsApi';
import getActiveAccessories from '../../../utils/activeAccessories';
import extractArrayData from '../../../utils/extractArrayData';
import LegalText from '../../UI/LegalText/LegalText';
import { ButtonType, InventoryItem } from '../../../utils/phoneStatus/types';
import {
  getGroupedButtonTypes,
  groupInventoryResponsesByColorAndSize,
  groupPhoneDetailsByColorAndSize,
  parseColorSizeStr,
} from '../../../utils/phoneStatus/getPhoneStatusCombos';
import { getMatchedOffersByColorAndSize } from '../../../utils/phoneStatus/offerUtils';
import getPhoneInventoryCacheKey from '../../../utils/phoneStatus/getPhoneInventoryCacheKey';
import Loader from '../../UI/Loader/Loader';
import { isKiosk } from '../../../utils/isKiosk';
import BackToPageLink from '../../UI/BackToPageLink/BackToPageLink';
import { useFeatureFlag } from '../../FeatureFlag/useFeatureFlag';
import { FeatureFlags } from '../../FeatureFlag/featureFlags';
import {
  AllContentfulPhoneBrand,
  CMSPageData,
  CombinatorSectionItem,
  ContentfulForm,
  ContentfulPhone,
  ContentfulPhoneReservation,
  EColour,
  Heading,
  InventoryLabels,
  PageSection,
} from '../../../ContentfulTypes';
import OfferDetails from './OfferDetails/OfferDetails';
import CarrierPricingDetails from './CarrierPricingDetails/CarrierPricingDetails';
import {
  Container,
  SectionHeading,
  GalleryWrapper,
  DeviceInfoWrapper,
  SpecsWrapper,
  PhoneTitle,
  ColourPickerContainer,
  MemoryContainer,
  PricingContainer,
  LegalNote,
  CompareWrapper,
  ComparePhoneButton,
  GetPhoneButton,
  ReservePhoneButton,
  PreorderPhoneButton,
  AdditionalInfo,
  CarrierPricingWrapper,
  CarrierPricingDetailsWrapper,
  LegalNoteSection,
  DeviceTypeWrapper,
  DevicePromoWrapper,
  LoadingWrapper,
  CtaWrapper,
} from './styles';

const ReservePhoneBlade = React.lazy(() => import('../../ReservePhoneBlade/ReservePhoneBlade'));

const MobileShopCards = React.lazy(() => import('../../MobileShopCards/MobileShopCards'));

const SpecsChart = React.lazy(() => import('./SpecsChart/SpecsChart'));

// This page has two versions. If a phone is active, it will render the full details of the offers, variants etc via this screen.
// If a phone is inactive (has no active variants) it will render a different version via the InactivePhoneScreen.

interface Props {
  data: CMSPageData;
  preferredProvince?: string;
  location: WindowLocation<{
    reserve?: boolean;
    reservableSize?: string;
    reservableColour?: string;
  }>;
}

const additionalInfoOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <AdditionalInfo>{children}</AdditionalInfo>
    ),
  },
};

// eslint-disable-next-line complexity
const PhoneDetailsScreen = ({
  preferredProvince,
  location,
  data,
}: Props) => {
  // TODO Add tests for this entire file
  const { locale } = useIntl();
  const isKioskMode = isKiosk();

  const isFFEnabledReserveAccessories = useFeatureFlag(FeatureFlags.ReserveAccessories);
  const isFFEnabledDualPricing = useFeatureFlag(FeatureFlags.DualPricing);

  const { reserveStatus, deviceType, displayTag } = data.contentfulPhone as ContentfulPhone;
  const { activeStore } = useActiveStore();
  const activeStoreId = useActiveStoreId();

  // Inventory threshold and labels
  const inventoryLabels = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.TypeName,
    'ContentfulPhoneInventoryThreshold',
  ) as InventoryLabels | undefined;

  // RUN PHONE THROUGH FORMATTER
  const formattedPhone = useFormatPhone({
    phone: data.contentfulPhone,
    province: preferredProvince,
  });
  // META DATA
  // ========================
  const phoneMetaTitle = formattedPhone.displayName.concat(
    data.contentfulPage?.metaTitle,
  );
  const phoneAvailableCarriers = formattedPhone.phoneVariants.map(
    (variant) => variant.phoneCarrier.displayName,
  );
  const carrierList = phoneAvailableCarriers
    .filter(
      (carrier, index) => phoneAvailableCarriers.indexOf(carrier) === index,
    )
    .toString()
    .replace(/,/g, ', ');

  const phoneMetaDescription = data.contentfulPage?.metaDescription?.metaDescription
    .replace('%PHONE_MODEL%', formattedPhone.displayName)
    .replace('%CARRIERS%', carrierList);

  // PHONE MODEL
  // ========================
  // Format phone name to separate and trim brand and model for display
  const brandDisplayName = formattedPhone.displayName.split(' ', 1)[0];
  const modelDisplayName = formattedPhone.displayName
    .replace(brandDisplayName, '')
    .trim();

  // SPEC LABELS
  // ========================
  const specLabelsSection = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.ContentfulId,
    '2kkZtaYPbAxkjZFGg4Qvl5',
  ) as ContentfulForm | undefined;
  const specLabels = specLabelsSection?.formFields.reduce((obj, item) => {
    if (item.displayLabel) {
      obj[item.contentful_id] = item.displayLabel;
    }
    return obj;
  }, {} as Record<string, string>);

  // VARIANT LABELS
  // ========================
  const variantLabelsSection = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.ContentfulId,
    '6SMq2KHEGmLVr3IBodCsN',
  ) as ContentfulForm | undefined;
  const variantLabels = variantLabelsSection?.formFields.reduce((obj, item) => {
    if (item.displayLabel) {
      obj[item.contentful_id] = item.displayLabel;
    }
    return obj;
  }, {} as Record<string, string>);

  // STORAGE
  // ========================
  const [availableSizeVariants, setAvailableSizeVariants] = useState(
    formattedPhone.availableSizes,
  );
  const [selectedStorageSize, setSelectedStorageSize] = useState(
    location?.state?.reservableSize || formattedPhone.availableSizes[0],
  );
  const handleStorageChange = useCallback((storageSize) => {
    setSelectedStorageSize(storageSize);
  }, [setSelectedStorageSize]);

  // Update colour options to match those available on variant
  const updateColourOptions = useCallback(() => {
    const variantAvailableColours: Array<EColour> = [];
    // for each available colour on phone model
    formattedPhone.availableColours.forEach((colour: EColour) => {
      formattedPhone.availableOffers.forEach((variant) => {
        // find colour variants available on selected size
        if (
          variant.memory === selectedStorageSize
            && variant.phoneColour.displayName === colour.displayName
        ) {
          variantAvailableColours.push(colour);
        }
      });
    });
    // remove duplicate colour variants
    const uniqueAvailableColours = variantAvailableColours.filter(
      (colour, index) => variantAvailableColours.indexOf(colour) === index,
    );
    setAvailableColourVariants(uniqueAvailableColours);
  }, [formattedPhone.availableColours, formattedPhone.availableOffers, selectedStorageSize]);
    
  // When storage size is updated, update available colours array to match
  useEffect(() => {
    updateColourOptions();
  }, [updateColourOptions]);

  // COLOURS
  // ========================
  // Array of colours that are available on current variants
  const [availableColourVariants, setAvailableColourVariants] = useState(
    formattedPhone.availableColours,
  );

  // Current active colour
  const [selectedColour, setSelectedColour] = useState(
    location?.state?.reservableColour
      || formattedPhone.availableColours[0].displayName,
  );

  // Update colour options to match those available on current size variant
  const updateSizeOptions = useCallback(() => {
    const { availableSizes, availableOffers } = formattedPhone;

    const uniqueAvailableSizes = availableSizes.filter((size) => availableOffers.find(
      ({ phoneColour, memory }) => phoneColour.displayName === selectedColour && memory === size,
    ));

    setAvailableSizeVariants(uniqueAvailableSizes);
  }, [formattedPhone, selectedColour]);

  // IMAGES
  // ========================
  // Find the image set that matches the active colour and store to state
  const getCurrentImageSet = useCallback((colourName) => {
    const newImageSet = formattedPhone.phoneImages.find(
      (images) => images.phoneColour.displayName === colourName,
    );
    setPhoneVariantImageSet(newImageSet);
  }, [formattedPhone.phoneImages]);
  const [phoneVariantImageSet, setPhoneVariantImageSet] = useState(
    formattedPhone.phoneImages[0],
  );
  
  // When selected colour updates, update current image set
  useEffect(() => {
    getCurrentImageSet(selectedColour);
    updateSizeOptions();
  }, [getCurrentImageSet, selectedColour, updateSizeOptions]);

  const handleColourChange = useCallback((colourName) => {
    setSelectedColour(colourName);
  }, [setSelectedColour]);

  // RESERVE A PHONE
  // ========================
  // Blade state
  const [reserveBladeVisible, setReserveBladeVisible] = useState(false);

  // Some links on the site pass state directing the reserve blade to be already opened on page load
  useEffect(() => {
    if (location.state && location.state.reserve === true) {
      setReserveBladeVisible(true);
      // clear location state so we don't keep reopening on page refresh
      const historyState = window.history.state;
      delete historyState.reserve;
      delete historyState.reservableColour;
      delete historyState.reservableSize;
      window.history.replaceState(historyState, document.title);
    }
  }, [location.state]);

  const payload = useInventoryPayload(formattedPhone.availableOffers);
  const { response, error, loading } = useTmsApi<ProductInventoryResponse>(
    '/inventory/product-inventory',
    payload,
    'post',
    getPhoneInventoryCacheKey({
      storeId: activeStoreId ?? '',
      displayName: formattedPhone.displayName,
    }),
  );

  // calculate the phone detail variables for all color and size combinations
  const phoneDetailsByColorAndSize = useMemo(
    () => {
      if (!response) {
      // need to return fallback values for the current colour and size
        const matchedOffers = getMatchedOffersByColorAndSize({
          availableOffers: formattedPhone.availableOffers,
          phoneColor: selectedColour,
          storageSize: selectedStorageSize,
        });
        return {
          [`${selectedColour}::${selectedStorageSize}`]: {
            matchedOffers,
            offersWithInventory: !loading ? [...matchedOffers] : [],
            reservableCarrierExists: false,
          },
        };
      }

      return groupPhoneDetailsByColorAndSize({
        offers: formattedPhone.availableOffers,
        reserveStatus,
        groupedInventoryItems: groupInventoryResponsesByColorAndSize({
          offers: formattedPhone.availableOffers,
          inventoryItems: response.response as InventoryItem[],
        }),
      });
    },
    [
      response,
      formattedPhone.availableOffers,
      reserveStatus,
      selectedColour,
      selectedStorageSize,
      loading,
    ],
  );

  const { offersWithInventory, matchedOffers } = phoneDetailsByColorAndSize[`${selectedColour}::${selectedStorageSize}`];

  const buttonTypesByColorAndSize = useMemo(() => getGroupedButtonTypes({
    groupedDetails: phoneDetailsByColorAndSize,
    isFFEnabledReserveAccessories,
    reserveStatus,
    loading,
    deprecatedFlags: {
      disablePhoneReservation:
          data.contentfulPhoneReservation?.disablePhoneReservation,
      enableReserveButton: formattedPhone.enableReserveButton,
    },
  }), [
    phoneDetailsByColorAndSize,
    isFFEnabledReserveAccessories,
    reserveStatus,
    loading,
    data.contentfulPhoneReservation?.disablePhoneReservation,
    formattedPhone.enableReserveButton,
  ]);

  // API call to get inStock count of listed phone
  const [apiError, setApiError] = useState(false);

  const isAlwaysReservable = reserveStatus === ReserveStatus.Always;

  useEffect(() => {
    if (error) {
      setApiError(true);
    }
  }, [error]);

  // Create offer components from those offers
  const matchedOfferComponents = useMemo(
    () => offersWithInventory.map((offer) => (
      <OfferDetails
        key={offer.cmsName}
        offer={offer}
        labels={variantLabels as Record<string, string>}
        inventoryStatus={
            isAlwaysReservable ? InventoryStatus.ShipToStore : offer?.status
          }
        inventoryLabels={inventoryLabels}
        activeStore={activeStore}
        apiError={apiError}
        selectedCarrier={offer?.phoneCarrier?.cmsName}
        reserveStatus={reserveStatus}
      />
    )),
    [
      activeStore,
      apiError,
      inventoryLabels,
      isAlwaysReservable,
      offersWithInventory,
      reserveStatus,
      variantLabels,
    ],
  );

  // Create carrier pricing components from those offers
  const matchedCarrierPricingComponents = useMemo(
    () => offersWithInventory.map((offer) => (
      <CarrierPricingDetails
        key={offer.cmsName}
        offer={offer}
        labels={variantLabels as Record<string, string>}
        inventoryStatus={
              isAlwaysReservable ? InventoryStatus.ShipToStore : offer?.status
            }
        inventoryLabels={inventoryLabels}
        activeStore={activeStore}
        apiError={apiError}
        selectedCarrier={offer?.phoneCarrier?.cmsName}
        reserveStatus={reserveStatus}
      />
    )),
    [
      activeStore,
      apiError,
      inventoryLabels,
      isAlwaysReservable,
      offersWithInventory,
      reserveStatus,
      variantLabels,
    ],
  );

  // Scroll to top of page on load
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 100);
    }
  }, []);

  // CALL TO ACTION CARDS
  // ========================
  // Call to action card section
  const cardsSection = data.contentfulPage?.pageSections.find((section) => section.contentful_id === '3uhHNOZLVcAjG3WnNjJodv');
  // Get card section header
  const headingSection = cardsSection?.combinatorSectionItems?.find((item) => item.__typename === 'ContentfulTextSection');
  // Get background images from data
  const backgroundImages = cardsSection?.combinatorSectionItems?.filter(
    (item) => item.__typename === 'ContentfulImageSection',
  );
  // Get cards from data
  const cards = cardsSection?.combinatorSectionItems?.filter((item) => item.__typename === 'ContentfulCardItem');
  // Get blades from data
  const bladeData = cardsSection?.combinatorSectionItems?.filter((item) => item.__typename === 'ContentfulBlade');

  const { baseUrl, currentPage } = useLocation();
  const schemaImage = phoneVariantImageSet?.phoneFrontImage?.asset?.localImage
    ?.publicUrl
    ? `${baseUrl}${phoneVariantImageSet?.phoneFrontImage?.asset?.localImage?.publicUrl}`
    : `${baseUrl}/tms-logo-${locale}.svg`;
  const schemaOffers = matchedOffers.map((offer) => ({
    '@type': 'Offer',
    url: currentPage,
    priceCurrency: 'CAD',
    price: offer.carrierUpfrontPrice,
    itemCondition: 'https://schema.org/NewCondition',
  }));
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: `${brandDisplayName} ${modelDisplayName}`,
    image: schemaImage,
    brand: 'The Mobile Shop',
    offers: schemaOffers,
  };

  const hasPcoPoints = useMemo(() => {
    const offerWithPcoPoints = offersWithInventory.some(({ pcoPoints }) => pcoPoints > 0);
    return offerWithPcoPoints;
  }, [offersWithInventory]);

  // Legal Section Default
  const {
    contentBody: legalTextSectionDefault,
    heading: legalTextHeadingDefault,
  } = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.ContentfulId,
    '2eRqxCLUZeeUIr8q7L1DyL',
  ) as PageSection;

  // Legal Section PCO Points
  const { contentBody: legalTextSectionPco, heading: legalTextHeadingPco } = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.ContentfulId,
    '7KuU6QrE2xznhvHPfUaBUE',
  ) as PageSection;

  // Legal Section Kiosk
  const { contentBody: legalTextSectionKiosk } = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.ContentfulId,
    '54L9TG8mWgnY2vP58uzhj8',
  ) as PageSection;

  const { heading: legalTextHeadingKiosk } = extractArrayData(
    data.contentfulPage?.pageSections,
    Contentful.ContentfulId,
    '54L9TG8mWgnY2vP58uzhj8',
  ) as PageSection;

  // Legal Section Scenarios
  let legalText = [legalTextSectionDefault];
  let legalHeading = legalTextHeadingDefault;
  if (hasPcoPoints) {
    legalText = [legalTextSectionPco];
    legalHeading = legalTextHeadingPco;
  } else if (isKioskMode) {
    legalText = [legalTextSectionKiosk];
    legalHeading = legalTextHeadingKiosk;
  }

  const buttonType = buttonTypesByColorAndSize[`${selectedColour}::${selectedStorageSize}`]
    ?? ButtonType.None;

  const reservableCombos = buttonType === ButtonType.Reserve
    ? Object.entries(buttonTypesByColorAndSize).reduce(
      (acc, [combo, type]) => {
        if (type !== ButtonType.Reserve) {
          return acc;
        }
        const { phoneColor, storageSize } = parseColorSizeStr(combo);
        return {
          ...acc,
          [phoneColor]: (acc[phoneColor] ?? []).concat(storageSize),
        };
      },
      {},
    )
    : {};
  const isColorReservable = (displayName) => Object.keys(reservableCombos).includes(displayName);
  const isReserveButton = buttonType === ButtonType.Reserve;
  const reservableColourVariants = isReserveButton
    ? formattedPhone.availableColours.filter(({ displayName }) => isColorReservable(displayName))
    : [];

  const reservableSizeVariants = isReserveButton ? reservableCombos[selectedColour] : [];

  const preorderFilter = formattedPhone.smartFilters.some(
    (phone) => phone.cmsName === 'standard-offers-preOrderPhones',
  );

  return (
    <>
      <Seo
        title={phoneMetaTitle}
        description={phoneMetaDescription}
        ldJson={schema}
      />
      <Container>
        {/* BACK TO PHONES */}
        <BackToPageLink
          label={ReactHtmlParser(specLabels?.['1JOKfYsM6SmBXibY0ewFey'])}
          to={`/${locale}/phones`}
        />

        {/* IMAGES */}
        <GalleryWrapper>
          <Gallery imageSet={phoneVariantImageSet} isPhoneActive />
        </GalleryWrapper>

        {/* PHONE OPTIONS */}
        <DeviceInfoWrapper>
          {deviceType === DeviceType.PrepaidPhone && (
            <DeviceTypeWrapper>{displayTag}</DeviceTypeWrapper>
          )}
          {displayTag && preorderFilter && (
            <DevicePromoWrapper>{displayTag}</DevicePromoWrapper>
          )}

          <PhoneTitle data-testid="phone-title">
            <span className="brand">{ReactHtmlParser(brandDisplayName)}</span>
            <span className="model">{ReactHtmlParser(modelDisplayName)}</span>
          </PhoneTitle>

          <ColourPickerContainer>
            <SectionHeading data-testid="section-heading">
              {ReactHtmlParser(variantLabels?.['4auM3OxXjstv6qdqUWuKPJ'])}
            </SectionHeading>
            <ColourPicker
              colours={availableColourVariants}
              selectedColour={selectedColour}
              onColourChange={handleColourChange}
              colourSelectable
            />
          </ColourPickerContainer>
          <MemoryContainer>
            <SectionHeading>
              {ReactHtmlParser(variantLabels?.SAz2sQ260Eomsbi4WjtG2)}
            </SectionHeading>
            <MemorySelector
              availableSizes={availableSizeVariants}
              selectedStorageSize={selectedStorageSize}
              selectSize={handleStorageChange}
            />
          </MemoryContainer>

          {/* OFFERS */}
          <PricingContainer>
            <SectionHeading className="pricing">
              {ReactHtmlParser(variantLabels?.['55skXeaYRFnZe3vpc35jtf'])}
            </SectionHeading>
            {loading ? (
              <LoadingWrapper>
                <Loader className="small m-auto" />
              </LoadingWrapper>
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {isFFEnabledDualPricing ? (
                  <CarrierPricingDetailsWrapper>
                    {!!offersWithInventory.length && matchedCarrierPricingComponents}
                  </CarrierPricingDetailsWrapper>
                ) : (
                  <CarrierPricingWrapper
                    id="pricingWrapper"
                    allowMultipleExpanded
                    allowZeroExpanded
                  >
                    {!!offersWithInventory.length && matchedOfferComponents}
                  </CarrierPricingWrapper>
                )}
              </>
            )}
          </PricingContainer>

          {/* Legal notes */}
          {deviceType !== DeviceType.PrepaidPhone && (
            <LegalNoteSection>
              {/* Freedom */}
              {formattedPhone.availableOffers.some(
                (offer) => offer.phoneCarrier.cmsName === 'Freedom Mobile',
              ) && (
                <LegalNote>
                  {ReactHtmlParser(variantLabels?.['5T0n4OiVgjKO9VmZmVWGTS'])}
                </LegalNote>
              )}
              {/* Shaw */}
              {formattedPhone.availableOffers.some(
                (offer) => offer.phoneCarrier.cmsName === 'Shaw Mobile',
              ) && (
                <LegalNote>
                  {ReactHtmlParser(variantLabels?.['2b3dsutfwLYNvoGvn0q1uQ'])}
                </LegalNote>
              )}
              {/* Standard */}
              <LegalNote>
                {ReactHtmlParser(variantLabels?.muad6Hkn0J9M8dxgetDgw)}
              </LegalNote>
              {/* Pre-order */}
              {preorderFilter && (
                <LegalNote>
                  {ReactHtmlParser(variantLabels?.['0w92W07mmev1a2uQBFJ19'])}
                </LegalNote>
              )}
            </LegalNoteSection>
          )}

          {/* CTAs */}
          <CompareWrapper>
            {loading ? (
              <LoadingWrapper>
                <Loader className="small m-auto" />
              </LoadingWrapper>
            ) : (
              <>
                <ContextConsumer>
                  {({ selectedPhones, addPhone, removePhone }) => {
                    const isAlreadySelected = !!selectedPhones.find(
                      (mobilePhone) => mobilePhone.displayName
                        === formattedPhone.displayName,
                    );
                    const isDisabled = !isAlreadySelected && selectedPhones.length >= 3;
                    return (
                      <ComparePhoneButton
                        onClick={() => isAlreadySelected
                          ? removePhone(formattedPhone)
                          : addPhone(formattedPhone)}
                        disabled={isDisabled}
                        aria-live="polite"
                      >
                        {isAlreadySelected ? (
                          <span>
                            {ReactHtmlParser(
                              variantLabels?.['11k1VH9YDIEmCAob3ZKY9P'],
                            )}
                          </span>
                        ) : (
                          <>
                            <span aria-hidden="true">+&nbsp;</span>
                            <span>
                              {ReactHtmlParser(
                                variantLabels?.['7sbL6jgw1dDIL2GgIzr27L'],
                              )}
                            </span>
                          </>
                        )}
                      </ComparePhoneButton>
                    );
                  }}
                </ContextConsumer>
                {/* RENDER EITHER THE 'RESERVE NOW' OR 'GET THIS PHONE' BUTTON */}
                {loading ? (
                  <LoadingWrapper>
                    <Loader className="small m-auto" />
                  </LoadingWrapper>
                ) : (
                  <CtaWrapper>
                    {preorderFilter ? (
                      <PreorderPhoneButton
                        href={`/en/preorder?phoneModel=${formattedPhone.cmsName}`}
                        data-ga="cta-preorder-phones"
                        data-testid="preorder-button"
                        data-campaign-id={ReactHtmlParser(
                          variantLabels?.['6NgTHANHEuuCKUWjpFIvuy'],
                        )}
                        data-section={formattedPhone.cmsName}
                        data-cta-id={ReactHtmlParser(
                          variantLabels?.ALwCUXtSdJhNiN64Q0da1,
                        )}
                      >
                        {ReactHtmlParser(variantLabels?.ALwCUXtSdJhNiN64Q0da1)}
                      </PreorderPhoneButton>
                    ) : (
                      <>
                        {buttonType === ButtonType.Reserve && (
                          <ReservePhoneButton
                            type="button"
                            onClick={() => setReserveBladeVisible(true)}
                            data-ga="cta-add to basket-phones"
                            data-testid="add-to-basket-button"
                          >
                            {ReactHtmlParser(
                              variantLabels?.BCzsbwwL17WHYUUOEDKLf,
                            )}
                          </ReservePhoneButton>
                        )}
                        {buttonType === ButtonType.Chat && !isKiosk() && (
                          // This logic will possibly return, so please leave
                          // <GetPhoneButton
                          //   to={`/${locale}/contact-us/?form=assist`}
                          //   state={{ reserve: phone.disablePhoneReservationButton }}
                          // >
                          //   {ReactHtmlParser(variantLabels['6dSc9GbdeKCuQKk3owBWGf'])}
                          // </GetPhoneButton>
                          <GetPhoneButton
                            type="button"
                            onClick={openChatWidget}
                          >
                            {ReactHtmlParser(
                              variantLabels?.['18fDwhEqwBfa85shr9uoP4'],
                            )}
                          </GetPhoneButton>
                        )}
                      </>
                    )}
                  </CtaWrapper>
                )}
              </>
            )}
          </CompareWrapper>

          {/* If there is a block of "additonal info" display it */}
          <RichText
            document={formattedPhone.additionalDescription}
            options={additionalInfoOptions}
          />

          {/* COMPATIBLE ACCESSORIES */}
          <CompatibleAccessories
            data={getActiveAccessories(data.allContentfulAccessory?.nodes)}
            locale={locale}
            heading={ReactHtmlParser(variantLabels?.['6C1Q3T1bKiqHq2g9JbSHtQ'])}
            phone={data.contentfulPhone?.contentful_id}
          />
        </DeviceInfoWrapper>

        {/* SPECS CHART */}
        <Suspense>
          <SpecsWrapper>
            <SpecsChart
              phone={formattedPhone}
              specLabels={specLabels as Record<string, string>}
            />
          </SpecsWrapper>
        </Suspense>
        <Suspense>
          <ReservePhoneBlade
            reserveData={data.contentfulPhoneReservation as ContentfulPhoneReservation}
            phone={formattedPhone}
            bladeVisible={reserveBladeVisible}
            setBladeVisible={setReserveBladeVisible}
            availableOffers={offersWithInventory}
            variantLabels={variantLabels as Record<string, string>}
            selectedStorageSize={selectedStorageSize}
            selectSize={handleStorageChange}
            colours={reservableColourVariants}
            storageOptions={reservableSizeVariants}
            selectedColour={selectedColour}
            onColourChange={handleColourChange}
            inventoryLabels={inventoryLabels}
            page="reservationBlade"
            reserveStatus={reserveStatus}
            reservableCombos={reservableCombos}
            phoneBrands={data.allContentfulPhoneBrand as AllContentfulPhoneBrand}
          />
        </Suspense>
        <Suspense>
          <MobileShopCards
            heading={headingSection?.heading as Heading}
            cards={cards as Array<CombinatorSectionItem>}
            bladeData={bladeData as Array<CombinatorSectionItem>}
            backgroundImages={backgroundImages as Array<CombinatorSectionItem>}
          />
        </Suspense>
      </Container>
      <LegalText
        preExpand
        legalText={legalText}
        legalHeading={legalHeading}
        pageId="connect"
      />
    </>
  );
};

export default PhoneDetailsScreen;
